import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson } from '@factorialco/f0-react'
import {
  ProjectionKind,
  useMyTrainingRequestsDatatable,
} from 'generated/resources/datatable.graphql'
import { TrainingEmployeeForSelectFragment } from 'generated/resources/graphql'
import moment from 'moment'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { CompetenciesTags } from 'modules/trainings/components/MyTrainings/RequestsComponent/MyRequestsDataTable/CompetenciesTags'
import { applicantFilter } from 'modules/trainings/components/MyTrainings/RequestsComponent/MyRequestsDataTable/Filters/applicantFilter'
import { StatusTag } from 'modules/trainings/components/MyTrainings/RequestsComponent/MyRequestsDataTable/StatusTag'

interface Props {
  search: string
  employees: TrainingEmployeeForSelectFragment[]
}
export const Provider = ({ search, children, employees }: React.PropsWithChildren<Props>) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const source = useMyTrainingRequestsDatatable(
    [
      {
        identifier: 'trainingName',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.my_requests.datatable.columns.training_name'),
        render: ({ trainingName }) => trainingName,
        template: '{{ trainingName }}',
      },
      {
        identifier: 'competencies',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.my_requests.datatable.columns.competencies'),
        render: ({ competencies }) => <CompetenciesTags competencies={competencies} />,
        template: '{{ competencyIds }}',
      },
      {
        identifier: 'createdAt',
        kind: ProjectionKind.Custom,
        sortBy: 'createdAt',
        title: i18n.t('trainings.training_requests.my_requests.datatable.columns.request_date'),
        render: ({ createdAt }) => {
          const date = moment(createdAt)
          return <>{dateToString(date, 'ui')}</>
        },
        template: '{{ createdAt }}',
      },
      {
        identifier: 'authorId',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.my_requests.datatable.columns.applicant'),
        render: ({ author }) => (
          <Box gap="s16" flexDirection="row" alignItems="center">
            <F0AvatarPerson
              firstName={author?.fullName ?? ''}
              lastName=""
              src={buildUrl(author?.avatar ?? '')}
              size="medium"
            />
            <Text size="300">{author?.fullName}</Text>
          </Box>
        ),
        template: '{{ authorId }}',
      },
      {
        identifier: 'status',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_requests.my_requests.datatable.columns.status'),
        render: ({ status }) => <StatusTag status={status} />,
        template: '{{ status }}',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 120,
        render: ({ id, status }) => {
          return (
            <Box flexDirection="row" gap="s8">
              <Button
                type="secondary"
                iconName="arrow-right"
                onClick={() => {
                  if (status === 'draft') {
                    navigate(navigation.myTrainings.requests.detail.view.fullPath({ id }))
                  } else {
                    navigate(
                      navigation.myTrainings.requests.list.requestDetailSidepanel.fullPath({ id })
                    )
                  }
                  track(trackingEvents.training.trainingRequest.goToTrainingRequest, {
                    from: 'my_requests',
                  })
                }}
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        search,
        employeesOrAuthor: true,
        companyId,
      },
      defaultSort: 'createdAt',
    }
  )

  return (
    <DataTable.Provider
      sort={{
        path: 'createdAt',
        direction: 'desc',
      }}
      filters={[applicantFilter({ source: source, employees: employees })]}
      source={source}
    >
      {children}
    </DataTable.Provider>
  )
}
